import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Trash2, 
  GripVertical, 
  Eye, 
  EyeOff,
  Save,
  Settings,
  ChevronLeft,
  ChevronRight,
  Check,
  AlertCircle,
  Copy,
  FileText,
  Radio,
  CheckSquare,
  Type,
  Mail,
  Phone,
  Calendar,
  Upload,
  List,
  Hash,
  Globe,
  Sparkles,
  Wand2,
  Building,
  Factory,
  ShoppingBag,
  Heart,
  GraduationCap,
  Briefcase
} from 'lucide-react';
import { toast } from 'react-toastify';

// Templates por indústria
const industryTemplates = {
  tech: {
    name: "Tecnologia",
    icon: <Globe className="w-5 h-5" />,
    description: "Template para empresas de tecnologia e startups",
    fields: []
  },
  manufacturing: {
    name: "Indústria",
    icon: <Factory className="w-5 h-5" />,
    description: "Template para indústrias e manufaturas",
    fields: []
  },
  retail: {
    name: "Varejo",
    icon: <ShoppingBag className="w-5 h-5" />,
    description: "Template para lojas e e-commerce",
    fields: []
  },
  healthcare: {
    name: "Saúde",
    icon: <Heart className="w-5 h-5" />,
    description: "Template para hospitais e clínicas",
    fields: []
  },
  education: {
    name: "Educação",
    icon: <GraduationCap className="w-5 h-5" />,
    description: "Template para escolas e universidades",
    fields: []
  },
  corporate: {
    name: "Corporativo",
    icon: <Briefcase className="w-5 h-5" />,
    description: "Template genérico para empresas",
    fields: []
  }
};

// Tipos de campos disponíveis
const fieldTypes = [
  { value: 'text', label: 'Texto', icon: <Type className="w-4 h-4" /> },
  { value: 'email', label: 'E-mail', icon: <Mail className="w-4 h-4" /> },
  { value: 'tel', label: 'Telefone', icon: <Phone className="w-4 h-4" /> },
  { value: 'number', label: 'Número', icon: <Hash className="w-4 h-4" /> },
  { value: 'date', label: 'Data', icon: <Calendar className="w-4 h-4" /> },
  { value: 'textarea', label: 'Texto Longo', icon: <FileText className="w-4 h-4" /> },
  { value: 'radio', label: 'Escolha Única', icon: <Radio className="w-4 h-4" /> },
  { value: 'checkbox', label: 'Múltipla Escolha', icon: <CheckSquare className="w-4 h-4" /> },
  { value: 'select', label: 'Lista Suspensa', icon: <List className="w-4 h-4" /> },
  { value: 'file', label: 'Upload de Arquivo', icon: <Upload className="w-4 h-4" /> }
];

// Campos obrigatórios para analytics (não podem ser removidos ou ter required alterado)
const REQUIRED_ANALYTICS_FIELDS = [
  'identificacao',
  'tipo_denuncia', 
  'local_ocorrencia',
  'quando_ocorreu',
  'relato_detalhado'
];

const FormBuilder = ({ initialForm = null, onSave, onCancel }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    client_slug: '',
    client_name: '',
    is_active: true,
    fields: [],
    selectedTemplate: null
  });
  const [showPreview, setShowPreview] = useState(false);
  const [errors, setErrors] = useState({});
  const [isDragging, setIsDragging] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);

  // Inicializar com dados existentes ou template padrão
  useEffect(() => {
    if (initialForm) {
      setFormData(initialForm);
    } else {
      // Adicionar campos obrigatórios padrão
      setFormData(prev => ({
        ...prev,
        fields: getDefaultRequiredFields()
      }));
    }
  }, [initialForm]);

  // Campos obrigatórios padrão para analytics
  const getDefaultRequiredFields = () => [
    {
      id: Date.now() + 1,
      type: "radio",
      label: "Você gostaria de se identificar?",
      name: "identificacao",
      required: true,
      isAnalyticsRequired: true,
      options: ["Sim", "Não"],
      placeholder: ""
    },
    {
      id: Date.now() + 2,
      type: "radio",
      label: "O que você está denunciando?",
      name: "tipo_denuncia",
      required: true,
      isAnalyticsRequired: true,
      options: [
        "Assédio moral",
        "Assédio sexual",
        "Corrupção ou suborno",
        "Descumprimento de norma",
        "Discriminação",
        "Fraude",
        "Conflito de interesses",
        "Uso indevido de recursos",
        "Violação de segurança",
        "Outra irregularidade"
      ],
      placeholder: ""
    },
    {
      id: Date.now() + 3,
      type: "radio",
      label: "Onde ocorreu?",
      name: "local_ocorrencia",
      required: true,
      isAnalyticsRequired: true,
      options: ["Sede principal", "Filial", "Home office", "Cliente", "Fornecedor", "Outro"],
      placeholder: ""
    },
    {
      id: Date.now() + 4,
      type: "radio",
      label: "Quando ocorreu?",
      name: "quando_ocorreu",
      required: true,
      isAnalyticsRequired: true,
      options: [
        "Hoje",
        "Ontem", 
        "Esta semana",
        "Este mês",
        "Nos últimos 3 meses",
        "Nos últimos 6 meses",
        "Há mais de 6 meses"
      ],
      placeholder: ""
    },
    {
      id: Date.now() + 5,
      type: "textarea",
      label: "Descreva detalhadamente o que aconteceu",
      name: "relato_detalhado",
      required: true,
      isAnalyticsRequired: true,
      options: [],
      placeholder: "Por favor, forneça o máximo de detalhes possível sobre o incidente..."
    }
  ];

  // Wizard steps
  const steps = [
    { title: "Informações Básicas", icon: <FileText className="w-5 h-5" /> },
    { title: "Escolher Template", icon: <Wand2 className="w-5 h-5" /> },
    { title: "Configurar Campos", icon: <Settings className="w-5 h-5" /> },
    { title: "Revisar e Salvar", icon: <Check className="w-5 h-5" /> }
  ];

  // Validação do passo atual
  const validateStep = () => {
    const newErrors = {};
    
    if (currentStep === 0) {
      if (!formData.title) newErrors.title = "Título é obrigatório";
      if (!formData.client_name) newErrors.client_name = "Nome do cliente é obrigatório";
      if (!formData.client_slug) newErrors.client_slug = "Slug é obrigatório";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Navegação do wizard
  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  // Aplicar template selecionado
  const applyTemplate = (templateKey) => {
    const template = industryTemplates[templateKey];
    setFormData(prev => ({
      ...prev,
      selectedTemplate: templateKey,
      fields: [
        ...getDefaultRequiredFields(),
        ...template.fields
      ]
    }));
    toast.success(`Template "${template.name}" aplicado com sucesso!`);
    handleNext();
  };

  // Adicionar novo campo
  const addField = (type) => {
    const newField = {
      id: Date.now(),
      type: type,
      label: '',
      name: `field_${Date.now()}`,
      required: false,
      placeholder: '',
      options: type === 'radio' || type === 'checkbox' || type === 'select' ? ['Opção 1'] : [],
      isAnalyticsRequired: false
    };
    
    setFormData(prev => ({
      ...prev,
      fields: [...prev.fields, newField]
    }));
  };

  // Atualizar campo
  const updateField = (fieldId, updates) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.map(field => 
        field.id === fieldId 
          ? { ...field, ...updates }
          : field
      )
    }));
  };

  // Remover campo
  const removeField = (fieldId) => {
    const field = formData.fields.find(f => f.id === fieldId);
    
    // Não permitir remover campos obrigatórios para analytics
    if (field?.isAnalyticsRequired) {
      toast.error("Este campo é obrigatório para o sistema de analytics e não pode ser removido");
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.filter(f => f.id !== fieldId)
    }));
  };

  // Duplicar campo
  const duplicateField = (fieldId) => {
    const fieldToDuplicate = formData.fields.find(f => f.id === fieldId);
    if (fieldToDuplicate) {
      const newField = {
        ...fieldToDuplicate,
        id: Date.now(),
        name: `${fieldToDuplicate.name}_copy`,
        label: `${fieldToDuplicate.label} (Cópia)`,
        isAnalyticsRequired: false // Cópias não são obrigatórias para analytics
      };
      
      setFormData(prev => ({
        ...prev,
        fields: [...prev.fields, newField]
      }));
    }
  };

  // Drag and Drop
  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    setIsDragging(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedItem === null) return;
    
    const draggedField = formData.fields[draggedItem];
    const newFields = [...formData.fields];
    
    // Remover o item arrastado
    newFields.splice(draggedItem, 1);
    
    // Inserir na nova posição
    newFields.splice(dropIndex, 0, draggedField);
    
    setFormData(prev => ({
      ...prev,
      fields: newFields
    }));
    
    setDraggedItem(null);
    setIsDragging(false);
  };

  // Salvar formulário
  const handleSave = () => {
    if (!validateStep()) return;
    
    // Validar que tem pelo menos os campos obrigatórios
    const hasAllRequired = REQUIRED_ANALYTICS_FIELDS.every(fieldName =>
      formData.fields.some(f => f.name === fieldName && f.required)
    );
    
    if (!hasAllRequired) {
      toast.error("Campos obrigatórios para analytics estão faltando ou não marcados como obrigatórios");
      return;
    }
    
    onSave(formData);
    toast.success("Formulário salvo com sucesso!");
  };

  // Renderizar conteúdo do step
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Informações do Formulário</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Título do Formulário *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                  errors.title ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Ex: Canal de Denúncias - Empresa XYZ"
              />
              {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descrição
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                rows="3"
                placeholder="Descreva o propósito deste formulário..."
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome do Cliente *
                </label>
                <input
                  type="text"
                  value={formData.client_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, client_name: e.target.value }))}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                    errors.client_name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Ex: Empresa XYZ"
                />
                {errors.client_name && <p className="text-red-500 text-sm mt-1">{errors.client_name}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Slug (URL) *
                </label>
                <input
                  type="text"
                  value={formData.client_slug}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    client_slug: e.target.value.toLowerCase().replace(/\s+/g, '-')
                  }))}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                    errors.client_slug ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="ex: empresa-xyz"
                />
                {errors.client_slug && <p className="text-red-500 text-sm mt-1">{errors.client_slug}</p>}
                <p className="text-sm text-gray-500 mt-1">
                  URL do formulário: /denunciar/{formData.client_slug || 'slug'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                className="mr-2"
              />
              <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                Formulário ativo
              </label>
            </div>
          </div>
        );
        
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Escolha um Template</h3>
              <p className="text-gray-600">Comece com um template pré-configurado ou crie do zero</p>
            </div>
            
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(industryTemplates).map(([key, template]) => (
                <button
                  key={key}
                  onClick={() => applyTemplate(key)}
                  className={`p-4 border-2 rounded-lg hover:border-blue-500 transition-colors ${
                    formData.selectedTemplate === key ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex flex-col items-center space-y-2">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
                      {template.icon}
                    </div>
                    <h4 className="font-medium text-gray-900">{template.name}</h4>
                    <p className="text-xs text-gray-500 text-center">{template.description}</p>
                  </div>
                </button>
              ))}
            </div>
            
            <div className="text-center">
              <button
                onClick={handleNext}
                className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
              >
                Pular e criar do zero
              </button>
            </div>
          </div>
        );
        
      case 2:
        return (
          <div className="flex gap-6">
            {/* Painel de campos */}
            <div className="flex-1 space-y-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-gray-800">Campos do Formulário</h3>
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition flex items-center gap-2"
                >
                  {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  {showPreview ? 'Ocultar' : 'Ver'} Preview
                </button>
              </div>
              
              {/* Lista de campos */}
              <div className="space-y-3">
                {formData.fields.map((field, index) => (
                  <div
                    key={field.id}
                    draggable={!field.isAnalyticsRequired}
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, index)}
                    className={`p-4 bg-white border rounded-lg ${
                      isDragging && draggedItem === index ? 'opacity-50' : ''
                    } ${field.isAnalyticsRequired ? 'border-orange-300 bg-orange-50' : 'border-gray-200'}`}
                  >
                    <div className="flex items-start gap-3">
                      {!field.isAnalyticsRequired && (
                        <GripVertical className="w-5 h-5 text-gray-400 cursor-move mt-1" />
                      )}
                      
                      <div className="flex-1 space-y-3">
                        {field.isAnalyticsRequired && (
                          <div className="flex items-center gap-2 text-orange-600 text-sm mb-2">
                            <AlertCircle className="w-4 h-4" />
                            <span>Campo obrigatório para Analytics (não pode ser removido)</span>
                          </div>
                        )}
                        
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Tipo do Campo
                            </label>
                            <select
                              value={field.type}
                              onChange={(e) => updateField(field.id, { type: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                              disabled={field.isAnalyticsRequired}
                            >
                              {fieldTypes.map(type => (
                                <option key={type.value} value={type.value}>
                                  {type.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Nome do Campo
                            </label>
                            <input
                              type="text"
                              value={field.name}
                              onChange={(e) => updateField(field.id, { name: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                              placeholder="nome_campo"
                              disabled={field.isAnalyticsRequired}
                            />
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Label (Texto exibido)
                          </label>
                          <input
                            type="text"
                            value={field.label}
                            onChange={(e) => updateField(field.id, { label: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                            placeholder="Digite a pergunta ou label do campo"
                          />
                        </div>
                        
                        {(field.type === 'text' || field.type === 'textarea') && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Placeholder
                            </label>
                            <input
                              type="text"
                              value={field.placeholder}
                              onChange={(e) => updateField(field.id, { placeholder: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                              placeholder="Texto de exemplo..."
                            />
                          </div>
                        )}
                        
                        {(field.type === 'radio' || field.type === 'checkbox' || field.type === 'select') && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Opções (uma por linha)
                            </label>
                            <textarea
                              value={field.options.join('\n')}
                              onChange={(e) => updateField(field.id, { 
                                options: e.target.value.split('\n').filter(opt => opt.trim()) 
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                              rows="3"
                              placeholder="Opção 1&#10;Opção 2&#10;Opção 3"
                            />
                          </div>
                        )}
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id={`required_${field.id}`}
                              checked={field.required}
                              onChange={(e) => updateField(field.id, { required: e.target.checked })}
                              className="mr-2"
                              disabled={field.isAnalyticsRequired}
                            />
                            <label htmlFor={`required_${field.id}`} className="text-sm text-gray-700">
                              Campo obrigatório
                            </label>
                          </div>
                          
                          <div className="flex gap-2">
                            <button
                              onClick={() => duplicateField(field.id)}
                              className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                              title="Duplicar campo"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                            {!field.isAnalyticsRequired && (
                              <button
                                onClick={() => removeField(field.id)}
                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                                title="Remover campo"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Adicionar novo campo */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-3 text-center">Adicionar novo campo:</p>
                <div className="grid grid-cols-3 lg:grid-cols-5 gap-2">
                  {fieldTypes.map(type => (
                    <button
                      key={type.value}
                      onClick={() => addField(type.value)}
                      className="flex flex-col items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
                    >
                      <div className="text-gray-600 mb-1">{type.icon}</div>
                      <span className="text-xs text-gray-700">{type.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Preview */}
            {showPreview && (
              <div className="w-96 bg-gray-50 p-6 rounded-lg border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Preview do Formulário</h3>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h4 className="text-xl font-bold text-gray-900 mb-2">{formData.title || 'Título do Formulário'}</h4>
                  <p className="text-gray-600 mb-6">{formData.description || 'Descrição do formulário'}</p>
                  
                  <div className="space-y-4">
                    {formData.fields.map(field => (
                      <div key={field.id}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {field.label || 'Label do campo'}
                          {field.required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        
                        {field.type === 'textarea' ? (
                          <textarea
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                            placeholder={field.placeholder}
                            rows="3"
                            disabled
                          />
                        ) : field.type === 'select' ? (
                          <select className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" disabled>
                            <option>Selecione uma opção</option>
                            {field.options.map((opt, i) => (
                              <option key={i}>{opt}</option>
                            ))}
                          </select>
                        ) : field.type === 'radio' ? (
                          <div className="space-y-2">
                            {field.options.map((opt, i) => (
                              <label key={i} className="flex items-center">
                                <input type="radio" name={field.name} className="mr-2" disabled />
                                <span className="text-sm">{opt}</span>
                              </label>
                            ))}
                          </div>
                        ) : field.type === 'checkbox' ? (
                          <div className="space-y-2">
                            {field.options.map((opt, i) => (
                              <label key={i} className="flex items-center">
                                <input type="checkbox" className="mr-2" disabled />
                                <span className="text-sm">{opt}</span>
                              </label>
                            ))}
                          </div>
                        ) : (
                          <input
                            type={field.type}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                            placeholder={field.placeholder}
                            disabled
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
        
      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Revisão Final</h3>
              <p className="text-gray-600">Revise as configurações antes de salvar</p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-6 space-y-4">
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Informações do Formulário</h4>
                <div className="bg-white rounded p-4 space-y-2">
                  <p><span className="font-medium">Título:</span> {formData.title}</p>
                  <p><span className="font-medium">Cliente:</span> {formData.client_name}</p>
                  <p><span className="font-medium">URL:</span> /denunciar/{formData.client_slug}</p>
                  <p><span className="font-medium">Status:</span> 
                    <span className={`ml-2 px-2 py-1 rounded text-xs ${
                      formData.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {formData.is_active ? 'Ativo' : 'Inativo'}
                    </span>
                  </p>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Campos Configurados</h4>
                <div className="bg-white rounded p-4">
                  <p className="text-sm text-gray-600 mb-2">
                    Total de campos: <span className="font-medium">{formData.fields.length}</span>
                  </p>
                  <p className="text-sm text-gray-600 mb-2">
                    Campos obrigatórios: <span className="font-medium">
                      {formData.fields.filter(f => f.required).length}
                    </span>
                  </p>
                  <p className="text-sm text-gray-600">
                    Campos para analytics: <span className="font-medium text-orange-600">
                      {formData.fields.filter(f => f.isAnalyticsRequired).length}
                    </span>
                  </p>
                </div>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium mb-1">Importante:</p>
                    <p>Os campos marcados em laranja são obrigatórios para o funcionamento correto do sistema de analytics e relatórios. Eles não podem ser removidos ou ter a obrigatoriedade alterada.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-center gap-4">
              <button
                onClick={handlePrevious}
                className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
              >
                Voltar e Editar
              </button>
              <button
                onClick={handleSave}
                className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
              >
                <Save className="w-5 h-5" />
                Salvar Formulário
              </button>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Sparkles className="w-6 h-6 text-yellow-500" />
                Criador de Formulários Inteligente
              </h1>
              <p className="text-gray-600 mt-1">
                Configure seu canal de denúncias em minutos com nossa interface intuitiva
              </p>
            </div>
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition"
            >
              Cancelar
            </button>
          </div>
          
          {/* Steps indicator */}
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div
                key={index}
                className={`flex items-center ${index < steps.length - 1 ? 'flex-1' : ''}`}
              >
                <div className="flex items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    index < currentStep ? 'bg-green-500 text-white' :
                    index === currentStep ? 'bg-blue-600 text-white' :
                    'bg-gray-200 text-gray-600'
                  }`}>
                    {index < currentStep ? <Check className="w-5 h-5" /> : step.icon}
                  </div>
                  <span className={`ml-3 font-medium ${
                    index === currentStep ? 'text-blue-600' : 'text-gray-600'
                  }`}>
                    {step.title}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-1 mx-4 ${
                    index < currentStep ? 'bg-green-500' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>
        
        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          {renderStepContent()}
        </div>
        
        {/* Navigation */}
        {currentStep !== 3 && (
          <div className="bg-white rounded-lg shadow-sm p-6 mt-6">
            <div className="flex justify-between">
              <button
                onClick={handlePrevious}
                disabled={currentStep === 0}
                className={`px-6 py-2 rounded-lg transition flex items-center gap-2 ${
                  currentStep === 0 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <ChevronLeft className="w-4 h-4" />
                Anterior
              </button>
              
              <button
                onClick={handleNext}
                disabled={currentStep === steps.length - 1}
                className={`px-6 py-2 rounded-lg transition flex items-center gap-2 ${
                  currentStep === steps.length - 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                Próximo
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FormBuilder;